import { useCallback, useEffect, useRef, useState } from "react"
import { fetchSentimentUgc } from "../sentimentUgcApi"
import { SENTIMENT_POLL_INTERVAL_MS, SENTIMENT_MAX_POLL_ATTEMPTS } from "../constants"

// Mirrors SENTIMENT_UGC_API.md's job states:
//   processing -> nothing stored yet, keep polling
//   refreshing -> results present, a run is in flight (render + keep polling)
//   completed  -> settled, stop
//   failed     -> stop (any platform that did complete is still shown)
export const SENTIMENT_STATUS = {
  IDLE: "idle",         // not generated for this app yet - show the "Generate" button
  LOADING: "loading",   // first request in flight
  RUNNING: "running",   // job_status: processing - nothing to show yet
  READY: "ready",       // results to render (completed / refreshing / partial)
  FAILED: "failed",     // job_status: failed with nothing usable
  LOCKED: "locked",     // HTTP 403 - app limit / refresh needs upgrade, `error` holds the body
  ERROR: "error",       // any other error
}

// The first GET claims the app against the user's sentiment app limit, so it
// only ever fires from an explicit "Generate" click. Apps the user already
// generated are remembered (per browser) so revisiting one re-reads it
// automatically - re-reading a claimed app is free (SENTIMENT_UGC_API.md "Tiers").
const GENERATED_APPS_KEY = "ugcSentimentGeneratedApps"

const readGeneratedApps = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(GENERATED_APPS_KEY) || "[]")
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const rememberGeneratedApp = (appId) => {
  try {
    const apps = readGeneratedApps().filter(id => id !== appId)
    localStorage.setItem(GENERATED_APPS_KEY, JSON.stringify([appId, ...apps].slice(0, 200)))
  } catch {
    // Storage blocked (private window etc.) - the user just clicks Generate again.
  }
}

const hasPlatformResults = (result) =>
  !!result?.overall || Object.values(result?.platforms || {}).some(p => p?.status && p.status !== "pending")

/**
 * Loads an app's cross-platform comment sentiment on demand (`generate`) and
 * polls while the backend run is in flight. Resets whenever the app changes;
 * stale responses from a previous app are dropped.
 */
export default function useSentimentUgc(appId) {
  const [data, setData] = useState(null)
  const [status, setStatus] = useState(SENTIMENT_STATUS.IDLE)
  const [error, setError] = useState(null)
  // True while the backend reports a run in flight (processing/refreshing)
  // and we're still polling it.
  const [isRunning, setIsRunning] = useState(false)

  const pollTimeoutRef = useRef(null)
  const attemptsRef = useRef(0)
  const requestTokenRef = useRef(0)
  // Latest response, read from inside the poll callback, which would
  // otherwise close over a stale `data`.
  const dataRef = useRef(null)

  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }, [])

  const handleResult = useCallback((result, requestToken, poll) => {
    if (requestToken !== requestTokenRef.current) return
    rememberGeneratedApp(appId)
    dataRef.current = result
    setData(result)
    setError(null)

    const jobStatus = result?.job_status
    const inFlight = jobStatus === "processing" || jobStatus === "refreshing"

    if (jobStatus === "failed" && !hasPlatformResults(result)) {
      setStatus(SENTIMENT_STATUS.FAILED)
      setError(result?.error || result?.message || "Sentiment analysis failed. It can be retried in a few hours.")
      setIsRunning(false)
      return
    }

    setStatus(jobStatus === "processing" && !hasPlatformResults(result) ? SENTIMENT_STATUS.RUNNING : SENTIMENT_STATUS.READY)

    if (!inFlight) {
      attemptsRef.current = 0
      setIsRunning(false)
      return
    }

    attemptsRef.current += 1
    if (attemptsRef.current >= SENTIMENT_MAX_POLL_ATTEMPTS) {
      setIsRunning(false)
      if (!hasPlatformResults(result)) {
        setStatus(SENTIMENT_STATUS.FAILED)
        setError("This is taking longer than expected. The analysis keeps running - check back in a little while.")
      }
      return
    }
    setIsRunning(true)
    pollTimeoutRef.current = setTimeout(poll, SENTIMENT_POLL_INTERVAL_MS)
  }, [appId])

  const handleError = useCallback((err, requestToken) => {
    if (requestToken !== requestTokenRef.current) return
    setIsRunning(false)
    if (err.status === 403) {
      setStatus(SENTIMENT_STATUS.LOCKED)
      setError(err.data || {})
    } else {
      setStatus(SENTIMENT_STATUS.ERROR)
      setError(err.message || "Something went wrong.")
    }
  }, [])

  const poll = useCallback(async () => {
    if (!appId) return
    const requestToken = requestTokenRef.current
    try {
      const result = await fetchSentimentUgc({ appId })
      handleResult(result, requestToken, poll)
    } catch (err) {
      // A transient polling failure shouldn't wipe results already on screen.
      if (requestToken !== requestTokenRef.current) return
      if (err.status === 403 || !dataRef.current) handleError(err, requestToken)
      else setIsRunning(false)
    }
  }, [appId, handleResult, handleError])

  const generate = useCallback(async () => {
    if (!appId) return
    const requestToken = ++requestTokenRef.current
    stopPolling()
    attemptsRef.current = 0
    setError(null)
    setStatus(prev => (prev === SENTIMENT_STATUS.READY ? prev : SENTIMENT_STATUS.LOADING))
    try {
      const result = await fetchSentimentUgc({ appId })
      handleResult(result, requestToken, poll)
    } catch (err) {
      handleError(err, requestToken)
    }
  }, [appId, stopPolling, handleResult, handleError, poll])

  // New app: drop everything from the previous one, and re-read straight away
  // if this browser already generated it (free - the app is already claimed).
  useEffect(() => {
    requestTokenRef.current += 1
    stopPolling()
    attemptsRef.current = 0
    dataRef.current = null
    setData(null)
    setError(null)
    setIsRunning(false)
    setStatus(SENTIMENT_STATUS.IDLE)
    if (appId && readGeneratedApps().includes(appId)) generate()
    return stopPolling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId])

  return { data, status, error, isRunning, generate }
}
